import { cva, type VariantProps } from 'class-variance-authority';
import { LoaderCircle } from 'lucide-react';
import { Slot } from 'radix-ui';
import type { ComponentProps, MouseEvent, ReactNode } from 'react';
import { cn, focusRing } from '../lib/cn.js';

export const buttonVariants = cva(
  [
    'inline-flex shrink-0 select-none items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium',
    'transition-[background-color,border-color,color,box-shadow] duration-(--duration-fast) ease-standard',
    'disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50',
    'aria-busy:cursor-progress',
    '[&_svg]:pointer-events-none [&_svg]:size-[1.15em] [&_svg]:shrink-0',
    focusRing,
  ],
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-foreground shadow-xs hover:bg-primary-hover',
        secondary:
          'border border-border-strong bg-surface-raised text-fg shadow-xs hover:bg-surface-hover',
        accent: 'bg-accent text-accent-foreground shadow-xs hover:bg-accent-hover',
        ghost: 'text-fg hover:bg-surface-hover',
        /**
         * A destructive action on a page: outlined in danger, with an icon (pass `icon`), so it
         * never competes with the primary action. The filled form is for confirm dialogs only.
         */
        destructive:
          'border border-state-danger bg-surface-raised text-state-danger shadow-xs hover:bg-state-danger-subtle',
        /** The confirming button inside a destructive confirm dialog. */
        destructiveSolid:
          'bg-state-danger text-state-danger-foreground shadow-xs hover:bg-state-danger/90',
        link: 'h-auto px-0 text-primary-text underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-control-sm min-w-control-sm px-3 text-sm comfortable:text-body',
        md: 'h-control min-w-control px-control-x text-body',
        lg: 'h-control-lg min-w-control-lg px-6 text-base comfortable:text-xl',
        icon: 'size-control p-0',
      },
    },
    compoundVariants: [{ variant: 'link', className: 'h-auto min-w-0 px-0' }],
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export type ButtonProps = ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    /** Render the child element (e.g. a router Link) with button styling. */
    asChild?: boolean;
    /**
     * Shows a spinner and announces the busy state (`aria-busy`). The button keeps its full
     * colour (it isn't unavailable, it's working) but ignores clicks and form submits.
     */
    loading?: boolean;
    /** Icon before the label. */
    icon?: ReactNode;
  };

export function Button({
  className,
  variant,
  size,
  asChild = false,
  loading = false,
  disabled,
  icon,
  children,
  type,
  onClick,
  ...props
}: ButtonProps) {
  const classes = cn(buttonVariants({ variant, size }), className);
  if (asChild) {
    return (
      <Slot.Root className={classes} onClick={onClick} {...props}>
        {children}
      </Slot.Root>
    );
  }
  const handleClick = loading
    ? (event: MouseEvent<HTMLButtonElement>) => event.preventDefault()
    : onClick;
  return (
    <button
      type={type ?? 'button'}
      className={classes}
      disabled={disabled}
      aria-busy={loading || undefined}
      onClick={handleClick}
      {...props}
    >
      {loading ? <LoaderCircle aria-hidden className="animate-spin" /> : icon}
      {children}
    </button>
  );
}
